<script setup lang="ts">
const { locale, locales } = useI18n()
const switchLocalePath = useSwitchLocalePath()
const route = useRoute()
const config = useRuntimeConfig()
const currencyStore = useCurrencyStore()

watch(locale, async () => {
  // Update user_locale cookie when language changes
  const userLocaleCookie = useCookie('user_locale', {
    default: () => 'ru',
    maxAge: 60 * 60 * 24 * 365, // 1 year
    sameSite: 'lax',
    secure: true,
    path: '/'
  })
  userLocaleCookie.value = locale.value

  currencyStore.loaded = false
  await currencyStore.fetchCurrencies()
})

// Check if language detection is enabled from env
const isEnabled = computed(() => config.public.langDetectionEnabled === true)

// Language to flag mapping
const langFlags: Record<string, string> = {
  ru: '/images/flags/ru@2x.png',
  ky: '/images/flags/kg@2x.png'
}

const availableLocales = computed(() => {
  return locales.value.filter((i) => i.code !== locale.value)
})

const currentLocale = computed(() => {
  return locales.value.find((i) => i.code === locale.value)
})

const getFlagUrl = (code: string) => langFlags[code] || ''

// Generate locale-aware path that preserves dynamic route parameters
const getLocalizedPath = (localeCode: string) => {
  // For pages with dynamic parameters, we need to build the path manually
  if (route.params && Object.keys(route.params).length > 0) {
    const fitPath = localeCode === 'ru' ? route.path.slice(3) : `/${localeCode}${route.path}`
    return `${fitPath}`
  }

  // For static pages, use the built-in switchLocalePath
  return switchLocalePath(localeCode)
}
</script>

<template>
  <div v-if="isEnabled">
    <DropdownMenu :modal="false">
      <DropdownMenuTrigger as-child>
        <Button
          variant="gray"
          size="icon"
          class="w-auto gap-1 !px-2"
          :title="$t('ui.language_switcher.title')"
          :aria-label="$t('ui.language_switcher.title')"
        >
          <NuxtImg
            v-if="currentLocale?.code && getFlagUrl(currentLocale.code)"
            :src="getFlagUrl(currentLocale.code)"
            :alt="`${currentLocale.name} flag`"
            width="20"
            height="15"
            class="h-[15px] w-5 object-contain"
          />
          <span class="text-xs font-semibold uppercase">{{ currentLocale?.code }}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="min-w-[140px]">
        <DropdownMenuItem
          v-for="loc in availableLocales"
          :key="loc.code"
          class="cursor-pointer"
          @click="navigateTo(getLocalizedPath(loc.code), { external: true })"
        >
          <div class="flex items-center gap-2">
            <NuxtImg
              v-if="getFlagUrl(loc.code)"
              :src="getFlagUrl(loc.code)"
              :alt="`${loc.name} flag`"
              width="20"
              height="15"
              class="h-[15px] w-5 object-contain"
            />
            <span>{{ loc.name }}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
